import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Users, Target, BookOpen, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { transformedPlaybooks } from '../../features/courses/data/transformedPlaybooks';
import ProvenPlaybooksShowcase from './ProvenPlaybooksShowcase';

const PlaybooksPreview = () => {
  return (
    <section className="py-16 md:py-24 bg-white dark:bg-zinc-900">
      <div className="container px-4 md:px-6 max-w-7xl mx-auto">
        <ProvenPlaybooksShowcase />

        {/* Playbooks Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {transformedPlaybooks.map((playbook, index) => (
            <motion.div
              key={playbook.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.6,
                delay: index * 0.1,
              }}
              viewport={{ once: true }}
              className="group"
            >
              <div className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-md transition-all duration-300 hover:shadow-lg group-hover:-translate-y-2 dark:border-zinc-600/45 dark:bg-zinc-800 dark:shadow-[0_1px_0_0_rgba(255,255,255,0.05)_inset,0_14px_44px_-14px_rgba(0,0,0,0.5)] dark:hover:border-zinc-500/55 dark:hover:shadow-[0_1px_0_0_rgba(255,255,255,0.06)_inset,0_18px_48px_-12px_rgba(0,0,0,0.55)]">
                {/* Card Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={playbook.cardImage}
                    alt={playbook.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />

                  {/* Enrolled Users */}
                  <div className="absolute top-4 right-4 flex items-center gap-1 bg-black/70 text-white px-2 py-1 rounded-full text-xs">
                    <Users className="h-3 w-3" />
                    {playbook.totalEnrolledUsers}
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-zinc-900 dark:text-zinc-100 mb-3 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                    {playbook.title}
                  </h3>

                  <p className="text-zinc-600 dark:text-zinc-300 mb-4 line-clamp-3">{playbook.shortDescription}</p>

                  {/* Playbook Stats */}
                  <div className="flex items-center gap-4 mb-4 text-sm text-zinc-500 dark:text-zinc-400">
                    <div className="flex items-center gap-1">
                      <BookOpen className="h-4 w-4" />
                      {playbook.phases.length} phases
                    </div>
                    <div className="flex items-center gap-1">
                      <Target className="h-4 w-4" />
                      {playbook.totalTasks} tasks
                    </div>
                  </div>

                  {/* Outcomes */}
                  <div className="mb-6">
                    <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">What you'll achieve:</p>
                    <div className="space-y-1">
                      {playbook.outcomes.slice(0, 2).map((outcome, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400">
                          <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />
                          {outcome}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Action Buttons — stack on very narrow screens for tap targets */}
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <div className="min-w-0 flex-1">
                      <Button
                        asChild
                        variant="ghost"
                        className="h-10 w-full rounded-md border border-zinc-200 bg-transparent px-4 font-medium text-zinc-800 shadow-none hover:bg-zinc-50 hover:text-zinc-950 group/btn dark:border-zinc-500 dark:text-zinc-100 dark:hover:border-zinc-400 dark:hover:bg-white/[0.07] dark:hover:text-white dark:focus-visible:ring-zinc-500"
                      >
                        <Link to={`/playbook/${playbook.id}`} className="inline-flex w-full items-center justify-center">
                          Learn More
                          <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                        </Link>
                      </Button>
                    </div>

                    <div className="min-w-0 flex-1">
                      <Button
                        asChild
                        className="w-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-300"
                      >
                        <Link to="/auth/register">Start This Playbook</Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <p className="text-lg text-zinc-600 dark:text-zinc-300 mb-6">
            Ready to transform your product into a $10K MRR success story?
          </p>
          <Button
            asChild
            size="lg"
            className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-4 text-lg shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <Link to="/auth/register">
              Get Started Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default PlaybooksPreview;
